<template>
    <div class="recorder-container">
      <div class="image-section">
        <!-- Observe Message -->
        <div v-if="step === 0" class="observe-message">
          <p>Observe the picture of a firefly to understand the process.</p>
        </div>
  
        <div v-if="step === 1" class="prompt-section">
          <p class="prompt">Now describe the image using the microphone and camera.</p>
        </div>
  
        <!-- Image Display -->
        <img
          v-if="step >= 0 && showImage"
          src="https://i.natgeofe.com/k/39dae00d-aad0-4ad0-b21e-c79f9c1dcd59/firefly-full-body_3x2.jpg"
          alt="Demo image of a firefly"
          class="image-display fade-in"
          @load="onImageLoad"
        />
      </div>
  
      <div class="controls">
        <!-- Tutorial Steps -->
        <div v-if="step === 0" class="countdown highlighted-instruction">
          <p>Step 1: Observe the image to understand how it works.</p>
          <button @click="nextStep" class="btn btn-next">Next Step</button>
        </div>
  
        <!-- Recording Controls -->
        <div v-if="step === 1" class="recording-controls highlighted-instruction">
          <p>Step 2: When ready, click "Start Recording" to describe the image.</p>
          <button @click="startRecording" v-if="showRecordButton" class="btn btn-record">
            <i class="icon-record"></i> Start Recording
          </button>
        <button @click="stopRecording" v-if="showStopButton" class="btn btn-stop">
            <i class="icon-stop"></i> Stop
        </button>
            
        </div>
        <div v-if="recording" class="recording-status">
            <div class="status-message">
                <span class="dot"></span> Recording...
            </div>
        </div>
  
        
  
        <!-- Playback and Transcription -->
        <div v-if="audioUrl && step === 2" class="playback-section highlighted-instruction">
          <p>Step 3: Confirm the transcription.</p>
          <p v-if="transcription">
            <strong>Transcription:</strong> {{ transcription }}
          </p>
          <!-- Completion Message -->
          <p v-else>
            <strong>Oops!</strong> There was an issue processing your audio. Try again.
          </p>
        </div>

        <div v-if="demoComplete" class="completion-message">
            <p>Demo completed! You are now ready for the actual experiment.</p>
            <button @click="restartTutorial" class="btn btn-complete">Restart Tutorial</button>
            <button @click="startExperiment" class="btn btn-start">Start Experiment</button>
        </div>
        
      </div>
    </div>
  </template>
  
  <script>
  export default {
    data() {
      return {
        step: 0,
        showImage: true,
        showRecordButton: false,
        showStopButton: false,
        recording: false,
        audioChunks: [],
        videoChunks: [],
        mediaRecorder: null,
        videoStream: null,
        audioUrl: null,
        transcription: null,
        demoComplete:false,
      };
    },
    watch: {
      step(newStep) {
        if (newStep === 0) {
          this.showImage = false;
          this.showRecordButton = false;
          this.showStopButton=false;
          setTimeout(() => {
            this.showImage = true;
          }, 1000);
        } else if (newStep === 1) {
          this.showRecordButton = false;
          setTimeout(() => {
            this.showRecordButton = true;
          }, 1000);
        }

      },
    },
    methods: {
      onImageLoad() {
        console.log("Image loaded");
      },
      nextStep() {

        this.showImage=true;
        if (this.step === 0) {
          this.showImage = false;
          this.showRecordButton = false;
          this.showStopButton=false;
          setTimeout(() => {
            this.showImage = true;
          }, 1000);
        } else if (this.step === 2) {
          this.showRecordButton = false;
          this.showStopButton=false;
          setTimeout(() => {
            this.showRecordButton = true;
          }, 1000);
        }
        this.step++;
      },
      startRecording() {
        this.audioChunks = [];
        this.videoChunks = [];
        this.audioUrl = null;
        this.showRecordButton=false;
        this.showStopButton=true;
  
        navigator.mediaDevices
          .getUserMedia({ audio: true, video: true })
          .then((stream) => {
            this.videoStream = stream;
  
            this.mediaRecorder = new MediaRecorder(stream);
            this.mediaRecorder.ondataavailable = (event) => {
              if (event.data && event.data.size > 0) {
                this.audioChunks.push(event.data);
                this.videoChunks.push(event.data);
              }
            };
  
            this.mediaRecorder.onstop = this.handleRecordingStop;
            this.mediaRecorder.start();
            this.recording = true;
          })
          .catch((err) => console.error("Error accessing media devices:", err));
      },
      stopRecording() {
        if (this.mediaRecorder) {
          this.mediaRecorder.stop();
          this.recording = false;
  
          if (this.videoStream) {
            this.videoStream.getTracks().forEach((track) => track.stop());
          }
          this.showRecordButton=true;
          this.showStopButton=false;
          this.demoComplete=true;
          this.step = 2;
        }
      },
      handleRecordingStop() {
        const audioBlob = new Blob(this.audioChunks, { type: "audio/webm" });
        const videoBlob = new Blob(this.videoChunks, { type: "video/webm" });
        this.audioUrl = URL.createObjectURL(audioBlob);
  
        this.simulateTranscription(audioBlob, videoBlob);
      },
      simulateTranscription(audioBlob, videoBlob) {
        // Simulate transcription delay and result
        setTimeout(() => {
          this.transcription = "This is a simulated transcription of your description of the firefly image.";
        }, 2000);
        console.log(audioBlob);
        console.log(videoBlob);
      },
      restartTutorial() {
        this.step = 0;
        this.showImage = false;
        this.transcription = null;
        this.audioUrl = null;
        this.recording = false;
        this.demoComplete=false;
      },
      startExperiment() {
        this.$emit("demo-complete");
      },
    },
  };
  </script>
  
  <style scoped>
  /* General Styles */
  .recorder-container {
    display: flex;
    flex-direction: column;
    align-items: center;
    font-family: Arial, sans-serif;
    padding: 5%;
  }
  
  .image-section {
    width: 100%;
    max-width: 600px;
    position: relative;
  }
  
  .image-display {
    width: 100%;
    height: auto;
    border-radius: 10px;
    box-shadow: 0 4px 10px rgba(0, 0, 0, 0.2);
    animation: fade-in 1s ease-in-out;
  }
  
  @keyframes fade-in {
    from {
      opacity: 0;
    }
    to {
      opacity: 1;
    }
  }
  
  .controls {
    margin-top: 20px;
    width: 100%;
    text-align: center;
  }
  
  .btn {
    padding: 10px 15px;
    margin: 10px;
    font-size: 1em;
    border: none;
    border-radius: 5px;
    cursor: pointer;
  }
  
  .btn-record {
    background-color: #28a745;
    color: white;
  }
  
  .btn-stop {
    background-color: #dc3545;
    color: white;
  }
  
  .btn-next {
    background-color: #007bff;
    color: white;
  }
  
  .btn-complete {
    background-color: #ffc107;
    color: white;
  }
  
  .highlighted-instruction {
    background-color: #f9f9f9;
    border: 2px solid #007bff;
    padding: 15px;
    margin: 15px 0;
    border-radius: 8px;
    font-size: 1.1em;
    text-align: center;
    box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
  }
  </style>
  